import Blog from "../../layouts/Blog";
import "../../styles/Blog.css"
import hljs from 'highlight.js';
import 'highlight.js/styles/dark.css';
import {useEffect} from "react";
import {getRandomColor} from "../../utils/GetRandomColor";
import IconText from "../../components/IconText";
import {faCodeMerge, faFolderTree, faPersonRunning} from "@fortawesome/free-solid-svg-icons";
import architecture from "../../assets/img/blog/howIConvertedMyReactDjango/architecture.png"

export default function HowIConvertedMyReactDjango() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const imageStyle = {
        border: `5px solid ${getRandomColor()}`,
    };

    const rightContent = (
        <div>
            <h1>
                <IconText text={"How I Converted My React + Django Setup Into a Static GitHub Pages Site"}
                          bgColor={true}/>
            </h1>
            <div className={"blog-abstract"}>
                <p>
                    GitHub Pages supports custom domains for its free static hosting. I was
                    refactoring my template-based Django site into a React frontend with a Django REST Framework
                    backend when I learned about this. I realized I could use this to my advantage. I wanted to keep
                    Django’s admin panel for
                    content management but still host the frontend on GitHub Pages. Since my frontend didn’t rely on
                    POST requests anymore, I decided to “bake” the data and turn the whole setup into a static site.
                    Here is how I did it.
                </p>
            </div>

            <div className={"blog-body"}>
                <h1><IconText text={"Project Structure"} icon={faFolderTree}/></h1>
                <p>
                    This project uses a Django backend that exposes a REST API and a React frontend that consumes it.
                    During development, both run together through Docker Compose. Here is the project structure:
                </p>
                <pre><code className={"language-xml"}>{`├── backend
│   ├── api                     # Django app for REST API
│   ├── cgcom                   # Django project
│   ├── db.sqlite3              # SQLite database
│   ├── Dockerfile
│   ├── manage.py
│   ├── requirements.txt
│   └── staticfiles
├── frontend                    # React frontend
│   ├── Dockerfile
│   ├── node_modules
│   ├── package-lock.json
│   ├── package.json
│   ├── public
│   └── src
└── docker-compose.yml          # Docker Compose configuration`}</code></pre>

                <p>
                    The backend uses SQLite solely for content management through the Django admin panel, and I have no
                    interest in switching to another database. If I had chosen PostgreSQL or something similar, I would
                    have integrated it into the Docker setup as well.
                </p>

                <h1>
                    <IconText text={"Process"} icon={faPersonRunning}/>
                </h1>
                <p>
                    The first problem is getting data from the database into React without a running Django instance.
                    The answer is to generate static JSON files directly from the database. Since the frontend no longer
                    depends on POST requests, it only needs read-only data, and it makes no difference whether that
                    comes from a REST endpoint or a JSON file.

                </p>
                <p>
                    The script below exports the required tables from the database into JSON files that the React
                    frontend can consume.
                </p>

                <pre><code className={"language-python"}>{`import os
import django
import json

os.environ.setdefault('DJANGO_SETTINGS_MODULE', 'cgcom.settings')
django.setup()

from django.core.serializers.json import DjangoJSONEncoder
from api.models import GameReview, ToiletReview, AlbumReview, ...

output_path = '../frontend/src/data/'

def export_tables_to_json():
    models = {
        'game_reviews': GameReview,
        'toilet_reviews': ToiletReview,
        'album_reviews': AlbumReview,
        ...
    }
    
    for table_name, model in models.items():
        records = list(model.objects.all().values())
        
        file_path = os.path.join(output_path, f'{table_name}.json')
        with open(file_path, 'w') as json_file:
            json.dump(records, json_file, indent=4, cls=DjangoJSONEncoder)
        
        print(f'Exported {table_name} to {table_name}.json')
    
    print('All data exported successfully!')
    
if __name__ == '__main__':
    export_tables_to_json()`}</code></pre>

                <p>
                    Now the frontend needs to fetch data from these JSON files instead of the REST API. The original API
                    service remains in place for possible future use, and an environment variable controls which data
                    source is active. Below is an example of how the GameReviewService was modified.
                </p>

                <pre><code className={"language-javascript"}>{`import axios from 'axios';
import gameReviewsData from '../../data/game_reviews.json';

const API_URL = process.env.REACT_APP_API_URL
const STATIC_WEBSITE = process.env.REACT_APP_STATIC_WEBSITE === 'True'

const GameReviewService = {
    getAllReviews: async () => {
        try {
            if (STATIC_WEBSITE) {
            return gameReviewsData;
            } else {
                const response = await axios.get('{API_URL}/gamereviews/');
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching games:', error);
            throw error;
        }
        },

    getCount: async () => {
        try {
            if (STATIC_WEBSITE) {
            return gameReviewsData.length;
            } else {
                const response = await axios.get('{API_URL}/gamereviews/count/');
                return response.data.count;
            }
        } catch (error) {
            console.error('Error fetching review count:', error);
            throw error;
        }
    }
    };

export default GameReviewService;`}</code></pre>

                <p>
                    Now the static site can be built and deployed to GitHub Pages. GitHub Actions handles both the build
                    step and the deployment step automatically.
                </p>

                <h1>
                    <IconText text={"Continuous Deployment Pipeline"} icon={faCodeMerge}/>
                </h1>

                <img src={architecture} className={"blog-img"}
                     alt="Architecture" style={imageStyle}/>
                <p>
                    I have set up two separate repositories: one for the source code and one for the generated static
                    site. Each push to the main branch of the source code repository triggers a GitHub Actions workflow
                    that builds the React frontend and publishes the output to the main branch of the static site
                    repository. The first step in that workflow is setting up Python.
                </p>

                <pre><code className={"language-yaml"}>{`- name: Set up Python
  uses: actions/setup-python@v4
  with:
    python-version: '3.13.1'

- name: Install dependencies
  run: |
  cd backend
  pip install -r requirements.txt`}</code></pre>

                <p>
                    Now the environment is ready to run the data export script.
                </p>

                <pre><code className={"language-yaml"}>{`- name: Export data to JSON
  run: |
  cd backend
  python export_data.py`}</code></pre>

                <p>
                    Next up is setting up Node and building the frontend.
                </p>

                <pre><code className={"language-yaml"}>{`- name: Set up Node
  uses: actions/setup-node@v3
  with:
    node-version: '20'
    
  - name: Build React App
    env:
      REACT_APP_STATIC_WEBSITE: {{vars.REACT_APP_STATIC_WEBSITE}}
    run: |
      cd frontend
      npm install
      npm run build`}</code></pre>

                <p>
                    Before deploying to the static site repository, a <code>404.html</code> file is added to redirect
                    all requests to <code>index.html</code>, ensuring direct access to subpages works correctly.
                </p>

                <pre><code className={"language-yaml"}>{`- name: Create 404 fallback
  run: |
    cp frontend/build/index.html frontend/build/404.html`}</code></pre>

                <p>
                    Finally, the deployment step pushes the built static site to the static site repository. One thing
                    to
                    note is that the <code>cname</code> file is created to set up the custom domain, since force pushing
                    removes
                    it otherwise.
                </p>

                <pre><code className={"language-yaml"}>{`- name: Deploy to GitHub Pages repo
  uses: peaceiris/actions-gh-pages@v3
  with:
    personal_token: {{secrets.DEPLOY_TOKEN}}
    external_repository: mpospirit/cagrigokpunar.com
    publish_dir: './frontend/build'  
    publish_branch: main
    cname: cagrigokpunar.com`}</code></pre>

                <p>
                    And that’s it! With this setup, I can manage content through Django’s admin panel while hosting a
                    static
                    React frontend on GitHub Pages freely. This approach lets me use the best of both worlds without
                    needing a
                    running backend instance.
                </p>

            </div>
        </div>
    )

    return (
        <div className="how-i-converted-my-react-django-setup-into-a-static-github-pages-site-page">
            <Blog rightContent={rightContent}/>
        </div>
    )
}
